import { ThematicBar, useThematic } from './thematics/ThematicBar'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { AppIcon } from './ui/icon'
import { mdiFileImage, mdiFilePdfBox, mdiPlus, mdiPaperclip, mdiDelete, mdiCommentTextOutline, mdiPencilRuler, mdiVectorSquare, mdiLayersTriple, mdiLayersOutline } from '@mdi/js'
import { SECTION_ICONS } from '../lib/section-icons'

import { $api } from '../api/queryClient'
import { apiClient } from '../api/client'
import { fetchAllPages } from '../api/paginateAll'
import { useAssetTypes, useDocumentTypes, useUploadDocument } from '../hooks/useQueries'
import { Button } from './ui/button'
import { Tabs, TabsList, TabsTrigger } from './ui/tabs'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from './ui/alert-dialog'
import { FrameRegistrationsPanel } from './plan/FrameRegistrationsPanel'
import { PlanCandidateWizard } from './plan/PlanCandidateWizard'
import { DocumentPickerDialog, type AttachDocumentControlLabels } from './documents/AttachDocumentControl'
import { AnnotationTaggingPanel, type AnnotationDraft } from './plan/AnnotationTaggingPanel'
import { PlanCanvas } from './plan/PlanCanvas'
import { CompositePlanLayerPanel, type CompositePlanNetworkFamily } from './plan/CompositePlanLayerPanel'
import { VECTOR_LAYERS, loadLayers, loadPanelOpen, saveLayers, savePanelOpen, type CompositePlanLayers } from './plan/composite-plan-layers'
import { sceneFromPlanScene, type SceneDoc, type VectorValue } from './plan-editor/engine/scene'
import { AssetProperties } from './AssetProperties'
import type { components } from '../api/schema'
import { assetTypeCodeDescendsFrom } from '../lib/asset-type-inheritance'
import { EmptyState } from './ui/empty-state'
import { useCan, usePermissions } from '../contexts/PermissionsContext'
import { isLotTypeCode, lotBaseTypeCode } from '../lib/asset-type-tree'

type AssetRepresentation = components['schemas']['AssetRepresentation']
type PlanScene = components['schemas']['PlanScene']
type RepresentationCandidate = components['schemas']['RepresentationCandidate']
type Issue = components['schemas']['Issue'] & { flags?: string[]; properties?: Record<string, unknown> }

const MAX_BYTES = 20 * 1024 * 1024
const ACCEPTED = 'image/png,image/jpeg,application/pdf'
const FLOORPLAN_CODES = ['FLOOR_PLAN', 'UNIT_PLAN']
// The vector plan editor anchors a frame, which only floors and units do in v1
// (spec 2026-08-22-02 §1).
const PLAN_EDITABLE_TYPE_CODES = ['floor', 'unit']
// Asset types that may ask for an ASSET-CENTRIC plan scene — the subjects the
// backend's `?context=asset` read accepts (spec 2026-09-19-02), which is no
// longer the frame anchors (`frames.FrameAssetTypes`) but the location types
// that may own plan geometry (`assets.VectorGeometryTypeCodes`). A
// `common_area` anchors no frame yet outlines itself exactly as the unit beside
// it does, so it borrows its floor's frame like a lot. `room` is resolved
// transitively — see `sceneSubjectApplies` — so its sub-types count too.
// Asking any other asset for a scene is still a 400.
const SCENE_SUBJECT_TYPE_CODES = ['building_group', 'building', 'floor', 'unit', 'common_area']
// Asset types whose Plan tab ALSO offers their place in the nearest populated
// ancestor frame once their own frame holds something (spec 2026-09-23-02). A
// lot with its own scan, or an interior drawn in its own plan editor, still
// sits somewhere on its floor, and the asset-centric read stops climbing the
// moment the own frame is populated. A floor, a building or a site is opened
// for its own content, so they keep the one answer they always had.
const FLOOR_CONTEXT_TYPE_CODES = ['unit']
// `?rep=` value of the tab composing a lot's OWN frame when no scan of its own
// does it (spec 2026-09-23-02): rooms drawn in the lot's editor belong to other
// assets, so none of the lot's representation tabs shows them.
const INTERIOR_TAB_KEY = 'interior'

/** One scene composed into one tab: the base the tab lives under, and what it draws. */
interface ComposedScene {
    baseRep: AssetRepresentation
    /** The base belongs to an ancestor's frame, not to this asset (read-only). */
    inherited: boolean
    /** Own representations this tab already draws, which lose their own tab. */
    absorbed: Set<string>
    /** The frame the scene was built from — what an inherited tab is named after. */
    frameName: string
}

/**
 * Compose a scan and the geometry drawn on it into ONE tab.
 *
 * A scan and the plan traced over it are two representations of the same
 * surface, and a tab each showed neither: the drawn tab had walls floating in
 * white, the scan tab had none of the inventory. When the frame ties them
 * together — the raster is registered into it and geometry resolves into it —
 * the viewer shows what the editor shows, one picture.
 */
function composeScene(
    scene: PlanScene | undefined,
    doc: SceneDoc | null,
    representations: AssetRepresentation[],
): ComposedScene | null {
    const entries = scene?.entries ?? []
    if (entries.length === 0 || !doc) return null
    const ownUids = new Set(representations.map(r => r.uid))
    // The drawing surface has to belong to the frame this scene was built
    // from. Normally that is one of *this* asset's own rasters: a floor plan
    // inherited from the building above is not this tab's subject, and
    // cropping the composite to it would frame the wrong page.
    //
    // The exception is the scene the server built from an ANCESTOR's frame
    // (`subject_asset` says so, spec 2026-09-17-01). The lot has no scan of
    // its own on its floor — the floor's scan *is* the background base layer
    // it was drawn over — so there the inherited raster is the surface. The
    // framing worry the original rule answered is handled where it belongs
    // instead: the fit is computed from this asset's own geometry, not from
    // the whole page.
    const inherited = Boolean(scene?.subject_asset?.uid)
    const rasters = entries.filter(
        e => (e.representation.kind === 'pdf' || e.representation.kind === 'image')
            && (inherited || ownUids.has(e.representation.uid)),
    )
    const base = rasters.find(e => e.primary) ?? rasters[0]
    if (!base) return null
    // Nothing to compose without geometry — a lone scan stays a lone scan.
    if (!entries.some(e => e.representation.kind === 'vector')) return null
    // The scan has to be placeable: a missing aspect or a collapsed scale
    // would put the drawing somewhere it is not, which is worse than showing
    // the two apart.
    const underlay = doc.underlays.find(u => u.repUid === base.representation.uid)
    if (!underlay || !(underlay.height > 0) || !(underlay.transform.scale > 0)) return null
    // An inherited base is not in this asset's representation list, so it is
    // built from the scene entry. `uid`, `kind` and `value` are everything
    // `PlanCanvas` reads off a representation; nothing else is invented.
    const baseRep: AssetRepresentation | undefined = representations.find(r => r.uid === base.representation.uid)
        ?? (inherited
            ? {
                uid: base.representation.uid,
                kind: base.representation.kind,
                value: base.representation.value,
                created_at: '',
            } as AssetRepresentation
            : undefined)
    if (!baseRep) return null
    const sceneUids = new Set(entries.map(e => e.representation.uid))
    // The base belongs to an ancestor's frame, not to this asset: it has no
    // tab of its own in the list, it must not be deletable from here
    // (`DELETE /representations/{uid}` is org-scoped and would take the
    // floor's scan away from everyone), and it cannot be annotated.
    const borrowed = inherited && !ownUids.has(baseRep.uid)
    return {
        baseRep,
        inherited: borrowed,
        // Own representations the composite already draws; they lose their
        // separate tab because the merged one shows them.
        //
        // A BORROWED composite absorbs nothing. An annotation is stored
        // against a representation, and the borrowed one is the floor's — so
        // the composite cannot be the asset's annotation surface, and
        // swallowing the asset's own tab would leave it with no way to be
        // annotated at all. Its own drawing keeps its tab, which is where its
        // notes belong (spec 2026-09-17-01) — unless that drawing holds
        // nothing at all, annotations included, in which case it is withheld
        // from the strip and there is nothing to put out of reach
        // (spec 2026-09-18-06).
        absorbed: borrowed
            ? new Set<string>()
            : new Set(representations.filter(r => sceneUids.has(r.uid)).map(r => r.uid)),
        frameName: scene?.frame_asset?.name ?? '',
    }
}

/**
 * Does this drawn plan hold nothing of the asset's own? (spec 2026-09-18-06)
 *
 * A lot drawn on its floor's plan gets a `vector` representation whose entire
 * content is the lot's own footprint — one space, `asset_uid` the asset itself.
 * Presented on a tab of its own it is a grey rectangle: the lot's PLACEMENT on
 * the floor, shown as though it were a plan OF the lot, next to the *In
 * {frame}* tab that shows the same geometry where it is legible.
 *
 * Three clauses, all of them necessary:
 *  1. no fixtures — walls, openings or equipments are the asset's own drawing;
 *  2. no space belonging to anything else — a lone self-footprint is empty, a
 *     room drawn inside the lot is not;
 *  3. no annotation on it — an annotation is stored against a representation
 *     and the borrowed composite cannot carry one, so withholding an annotated
 *     tab would put the note out of reach for good.
 */
function drawnPlanHoldsNothingOfItsOwn(
    rep: AssetRepresentation,
    assetUid: string,
    annotationCount: number,
): boolean {
    if (rep.kind !== 'vector') return false
    if (annotationCount > 0) return false
    const value = (rep.value ?? {}) as VectorValue
    if ((value.walls?.length ?? 0) > 0) return false
    if ((value.openingEquipments?.length ?? 0) > 0) return false
    if ((value.equipments?.length ?? 0) > 0) return false
    return (value.spaces ?? []).every(
        s => (s.properties as { asset_uid?: string } | undefined)?.asset_uid === assetUid,
    )
}


/**
 * Entry point into the full-page plan editor (spec 2026-08-22-02 §1 / plan
 * step 4). Rendered both on a populated Plan tab ("Edit plan") and on its empty
 * state ("Draw from scratch"), so a floor with nothing but a scan and a floor
 * with nothing at all both reach the editor in one click.
 */
function PlanEditorLink({ org, assetUid, assetTypeCode, variant, label }: {
    org: string
    assetUid: string
    assetTypeCode?: string
    variant: 'default' | 'outline'
    label: string
}) {
    if (!assetTypeCode || !PLAN_EDITABLE_TYPE_CODES.includes(lotBaseTypeCode(assetTypeCode))) return null
    return (
        <Button variant={variant} size="sm" asChild data-testid="plan-open-editor">
            <Link
                to="/$org/assets/$assetUid/plan-editor"
                params={{ org, assetUid }}
                aria-label={label}
                title={label}
                onClick={(e) => e.stopPropagation()}
            >
                <AppIcon path={mdiPencilRuler} size="sm" className="md:mr-1" />
                <span className="hidden md:inline">{label}</span>
            </Link>
        </Button>
    )
}

interface AssetPlanViewerProps {
    org: string
    assetUid: string
    assetTypeCode?: string
}

export function AssetPlanViewer({ org, assetUid, assetTypeCode }: AssetPlanViewerProps) {
    const thematic = useThematic(org, assetUid, 'floor', 'grouper')
    const { t, i18n } = useTranslation('assets')
    // Who cannot write the asset (a resident, a viewer) gets no upload zone
    // on an empty plan: the API would refuse the upload.
    const { membershipKnown } = usePermissions()
    const canWriteAssets = useCan('assets.write') || !membershipKnown
    const queryClient = useQueryClient()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const navigate = useNavigate()
    const search = useSearch({ strict: false }) as { rep?: string; annotation?: string }

    const [error, setError] = useState<string | null>(null)
    const [uploading, setUploading] = useState(false)
    // A plan needs no upload when the drawing is already among the asset's
    // documents (or anywhere in the org, by pasted link): the picker turns an
    // existing document into a plan through the same detect → create chain.
    const [docPickerOpen, setDocPickerOpen] = useState(false)
    // The active representation tracks the `?rep=<uid>` search param so deep
    // links — e.g. from an issue's target — open the right tab.
    const activeRepUid = search.rep ?? null
    const setActiveRepUid = useCallback(
        (uid: string | null) => {
            navigate({
                search: (prev: Record<string, unknown>) => ({
                    ...prev,
                    rep: uid ?? undefined,
                }) as never,
                replace: true,
            })
        },
        [navigate],
    )
    /**
     * Clicking a drawn room on the plan opens that room's own page (user,
     * 2026-09-16). The floor's children were reachable only from the right
     * panel's list, which asks the reader to match a name against a drawing
     * they are already looking at.
     *
     * The asset being viewed is skipped: its own footprint is routinely the
     * outline everything else sits inside, and "navigate to where you already
     * are" reads as a dead click.
     */
    const followSceneAsset = useCallback(
        (uid: string) => {
            if (uid === assetUid) return
            navigate({ to: '/$org/assets/$assetUid', params: { org, assetUid: uid } })
        },
        [navigate, org, assetUid],
    )

    const [wizardCandidates, setWizardCandidates] = useState<{
        documentUid: string
        kind: 'pdf' | 'image'
        candidates: RepresentationCandidate[]
    } | null>(null)

    const [draftAnnotation, setDraftAnnotation] = useState<AnnotationDraft | null>(null)
    // Editing an annotation is URL-state — `?annotation=<uid>` opens the panel
    // and survives reload / share. The local state mirrors the resolved Issue
    // object once it's loaded into `annotations`.
    const editingAnnotationUid = search.annotation ?? null
    const setEditingAnnotation = useCallback(
        (uid: string | null) => {
            navigate({
                search: (prev: Record<string, unknown>) => ({
                    ...prev,
                    annotation: uid ?? undefined,
                }) as never,
                replace: false,
            })
        },
        [navigate],
    )
    const [deleteRepUid, setDeleteRepUid] = useState<string | null>(null)
    // Edit-geometry mode: when set, the panel is hidden and PlanCanvas exposes
    // vertex handles. The pendingGeometry is staged in the panel until save.
    const [editingGeometry, setEditingGeometry] = useState<components['schemas']['AnnotationGeometry'] | null>(null)
    const [editGeometryActive, setEditGeometryActive] = useState(false)

    // List representations for this asset
    const repsQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/representations',
        {
            params: { path: { orgCode: org, assetUid } },
        },
    )
    const representations: AssetRepresentation[] = useMemo(
        () => repsQuery.data?.representations ?? [],
        [repsQuery.data],
    )

    // Whether either property axis has anything to show. `AssetProperties`
    // renders its own card — including a "No properties found" placeholder —
    // so the decision has to be made here, before mounting it.
    const propsQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties',
        {
            params: { path: { orgCode: org, assetUid } },
        },
    )
    const [hasSurfaceProperties, hasOccupationProperties] = useMemo(() => {
        const rows = (propsQuery.data as { data?: { property_definition?: { property_category_code?: string } }[] } | undefined)?.data ?? []
        const categories = new Set(
            rows.map(r => r.property_definition?.property_category_code).filter(Boolean) as string[],
        )
        return [categories.has('surfaces'), categories.has('occupation')]
    }, [propsQuery.data])

    // ---- Composing a scan and the geometry drawn on it into ONE tab ----
    //
    // Tabs stay for what genuinely stands alone: a second, unregistered scan
    // is not "the same plan" and keeps its own tab. See `composeScene`.
    //
    // `context: 'asset'` is the READER's resolution (spec 2026-09-17-01): when
    // this asset's own frame is empty, the server answers with the nearest
    // populated ancestor frame instead, restricted to this asset's line of
    // descent. The plan editor deliberately does not send it: it edits a
    // frame, and inherited geometry it must not move has no business in it.
    //
    // An empty own frame is the COMMON state of a lot, not the only one: a lot
    // with a scan of its own, or an interior drawn in its own plan editor,
    // populates it — and then the answer is the lot's own frame and nothing
    // else. Its place on the floor is asked for separately below.
    //
    // The asset types are read here, above the query, because the gate below
    // resolves `room` through the inheritance chain rather than matching a code
    // set: an unnamed room reads as its type and number on the drawing too
    // (spec 2026-08-31-02 §2), and both uses share this one load.
    const { data: assetTypes = [] } = useAssetTypes(org)
    const sceneSubjectApplies = !!assetTypeCode
        && (SCENE_SUBJECT_TYPE_CODES.includes(lotBaseTypeCode(assetTypeCode))
            || assetTypeCodeDescendsFrom(assetTypes, assetTypeCode, 'room'))
    const sceneQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/scene',
        { params: { path: { orgCode: org, assetUid }, query: { context: 'asset' as const } } },
        { enabled: sceneSubjectApplies },
    )
    // The asset-centric answer is this asset's OWN, populated frame.
    const ownFrameHoldsContent = !!sceneQuery.data
        && (sceneQuery.data.entries?.length ?? 0) > 0
        && !sceneQuery.data.subject_asset?.uid

    // …and then a lot still has a place on its floor (spec 2026-09-23-02):
    // `context: 'ancestor'` is the same borrowing with "the own frame wins"
    // skipped. Asked only when the first answer was the own frame — when it
    // already borrowed, the two reads are the same scene.
    const floorContextApplies = !!assetTypeCode
        && FLOOR_CONTEXT_TYPE_CODES.includes(lotBaseTypeCode(assetTypeCode))
        && ownFrameHoldsContent
    const floorSceneQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/scene',
        { params: { path: { orgCode: org, assetUid }, query: { context: 'ancestor' as const } } },
        { enabled: floorContextApplies },
    )

    // The frame's document, built by the **editor's** loader: the viewer draws
    // the exact same `SceneDoc` the editor edits, so the two cannot disagree on
    // where a wall is or what colour it is.
    const sceneDoc = useMemo(
        () => (sceneQuery.data ? sceneFromPlanScene(sceneQuery.data) : null),
        [sceneQuery.data],
    )
    const floorSceneDoc = useMemo(
        () => (floorContextApplies && floorSceneQuery.data?.subject_asset?.uid
            ? sceneFromPlanScene(floorSceneQuery.data)
            : null),
        [floorContextApplies, floorSceneQuery.data],
    )

    // The asset-centric scene composed: the own frame's composite, or the
    // borrowed one when the own frame was empty.
    const merged = useMemo(
        () => composeScene(sceneQuery.data, sceneDoc, representations),
        [sceneQuery.data, sceneDoc, representations],
    )
    // The floor's view of a lot whose own frame is populated. Kept only when it
    // really is borrowed — its base is the floor's, not one of the lot's own —
    // since otherwise there is nothing the own tabs do not already show.
    const floorContext = useMemo(() => {
        if (!floorSceneDoc) return null
        const composed = composeScene(floorSceneQuery.data, floorSceneDoc, representations)
        return composed?.inherited ? composed : null
    }, [floorSceneQuery.data, floorSceneDoc, representations])

    // THE *"In {frame}"* tab, whichever read produced it. Everything that
    // treats a borrowed composite as read-only keys on this.
    const inheritedView: ComposedScene | null = merged?.inherited ? merged : floorContext
    const inheritedDoc: SceneDoc | null = merged?.inherited ? sceneDoc : floorSceneDoc
    const isBorrowedTab = (uid: string | undefined) => !!uid && inheritedView?.baseRep.uid === uid

    // The lot's own interior, when no scan of its own composes it (spec
    // 2026-09-23-02). Rooms drawn in a lot's plan editor are other assets'
    // geometry resolved into the lot's frame, so no tab of the lot's own
    // showed them — the only way to see an interior was to open the editor.
    // It is a read-only composite: an annotation is stored against ONE
    // representation, and this picture is several.
    const interiorRep = useMemo<AssetRepresentation | null>(() => {
        if (!ownFrameHoldsContent || merged) return null
        if (!assetTypeCode || !FLOOR_CONTEXT_TYPE_CODES.includes(lotBaseTypeCode(assetTypeCode))) return null
        const ownUids = new Set(representations.map(r => r.uid))
        const foreign = (sceneQuery.data?.entries ?? []).some(
            e => e.representation.kind === 'vector' && !ownUids.has(e.representation.uid),
        )
        if (!foreign) return null
        return { uid: INTERIOR_TAB_KEY, kind: 'vector', value: {}, created_at: '' } as AssetRepresentation
    }, [ownFrameHoldsContent, merged, assetTypeCode, representations, sceneQuery.data])

    // While the composite is BORROWED, the asset's own drawn plan is a
    // candidate for being withheld — and the third emptiness clause is its
    // annotation count, which the header only knows for the ACTIVE tab, and
    // this one is not it. So it is asked for here, and only here: the query is
    // enabled in the borrowed case alone. There is one own drawing in practice
    // (the placement the plan editor wrote); a second would keep its tab.
    const ownVectorRep = useMemo(
        () => (inheritedView ? representations.find(r => r.kind === 'vector') ?? null : null),
        [inheritedView, representations],
    )
    const ownVectorAnnotationsQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues',
        {
            params: {
                path: { orgCode: org },
                query: {
                    target_entity_type: 'asset_representation',
                    target_entity_uid: ownVectorRep?.uid,
                    type: 'annotation',
                    status: 'draft,open,in_progress,closed',
                    assignee_uid: 'all',
                    limit: 200,
                } as unknown as Record<string, unknown>,
            },
        },
        { enabled: !!ownVectorRep?.uid },
    )
    // `undefined` while the count is unknown: a tab is withheld on an answer,
    // never on a guess, so nothing flickers away and comes back.
    const ownVectorAnnotationCount: number | undefined = ownVectorAnnotationsQuery.data
        ? (ownVectorAnnotationsQuery.data.data ?? []).length
        : undefined

    // WITHHELD FROM THE STRIP, NOT WITHDRAWN FROM EXISTENCE (spec 2026-09-18-06).
    //
    // This is deliberately NOT `absorbed`: absorbed means "another tab shows
    // this representation", and the borrowed composite shows nothing of the
    // asset's — it cannot even carry its annotations. The uid is only left out
    // of the tab LIST. `?rep=` at it still opens it, annotatable exactly as
    // before, and the absorbed-rewrite effect below must never move that link
    // onto the *In {frame}* tab: a tab hidden for having no annotations could
    // otherwise never receive its first one.
    const withheldRepUid = useMemo(() => {
        if (!ownVectorRep || ownVectorAnnotationCount === undefined) return null
        return drawnPlanHoldsNothingOfItsOwn(ownVectorRep, assetUid, ownVectorAnnotationCount)
            ? ownVectorRep.uid
            : null
    }, [ownVectorRep, ownVectorAnnotationCount, assetUid])

    // Hiding the geometry is what an annotator wants when the drawn plan sits
    // on top of the detail they are tagging.
    const [showVector, setShowVector] = useState(true)

    // Layer choices (patterns §44): the scanned sheets a frame stacks under
    // its redrawn plan can be faded or hidden one by one, or all at once.
    // Per viewer and per asset, so the floor someone reads "redrawn only"
    // stays that way.
    const [layerVisibility, setLayerVisibility] = useState<CompositePlanLayers>(() => loadLayers(org, assetUid))
    useEffect(() => {
        setLayerVisibility(loadLayers(org, assetUid))
    }, [org, assetUid])
    const changeLayerVisibility = useCallback((next: CompositePlanLayers) => {
        setLayerVisibility(next)
        saveLayers(org, assetUid, next)
    }, [org, assetUid])
    const [layerPanelOpen, setLayerPanelOpen] = useState(loadPanelOpen)
    const toggleLayerPanel = useCallback(() => {
        setLayerPanelOpen((open) => {
            savePanelOpen(!open)
            return !open
        })
    }, [])

    const localizedTypeLabel = useCallback((code: string): string => {
        const type = assetTypes.find((at: { code?: string }) => at?.code === code) as { name?: unknown } | undefined
        const name = type?.name
        if (typeof name === 'string') return name
        if (name && typeof name === 'object') {
            const m = name as Record<string, string>
            return m[i18n.language] || m.en || m.fr || code
        }
        return code
    }, [assetTypes, i18n.language])

    // Tabs: the merged one stands for every representation it absorbed, under
    // the base raster's uid so `?rep=` deep links keep resolving.
    const tabReps = useMemo(() => {
        const own = representations.filter(r => (!merged || r.uid === merged.baseRep.uid || !merged.absorbed.has(r.uid))
            // The lot's own drawing, while it holds nothing of its own: shown
            // only when the URL asks for it by uid, so that opening it stays
            // possible and the strip stays free of a grey rectangle nobody
            // drew (spec 2026-09-18-06).
            && (r.uid !== withheldRepUid || r.uid === activeRepUid))
        // An inherited base is not in `representations` at all, so it would
        // otherwise leave the tab strip with a selected value nothing matches.
        // It comes FIRST (spec 2026-09-23-02, resolved question 2): a reader
        // clicking into a lot is most often after where it is, and the lot's
        // own interior is one click away — right after it.
        return [
            ...(inheritedView ? [inheritedView.baseRep] : []),
            ...(interiorRep ? [interiorRep] : []),
            ...own,
        ]
    }, [representations, merged, withheldRepUid, activeRepUid, inheritedView, interiorRep])

    // Default tab: the floor's view of a lot when there is one (spec
    // 2026-09-23-02 — it opens first even when the lot has content of its
    // own); otherwise the merged picture; otherwise the lot's interior;
    // otherwise the drawn (vector) plan, which carries the user's own edits
    // that a raster of the source document cannot show.
    const defaultRep = useMemo(
        () => inheritedView?.baseRep
            ?? merged?.baseRep
            ?? interiorRep
            ?? representations.find(r => r.kind === 'vector')
            ?? representations[0]
            ?? null,
        [inheritedView, merged, interiorRep, representations],
    )

    // Pick active rep. A uid the merged tab absorbed resolves to the merged tab
    // straight away — waiting for the URL-rewriting effect below would flash
    // the old solo render for one frame.
    const activeRep = useMemo(() => {
        if (activeRepUid) {
            if (merged && merged.absorbed.has(activeRepUid)) return merged.baseRep
            const found = representations.find(r => r.uid === activeRepUid)
            if (found) return found
            // Neither of these is one of the asset's own representations.
            if (inheritedView && activeRepUid === inheritedView.baseRep.uid) return inheritedView.baseRep
            if (interiorRep && activeRepUid === interiorRep.uid) return interiorRep
        }
        return defaultRep
    }, [representations, activeRepUid, defaultRep, merged, inheritedView, interiorRep])
    const activeIsBorrowed = isBorrowedTab(activeRep?.uid)
    const activeIsInterior = !!interiorRep && activeRep?.uid === interiorRep.uid
    // What the canvas composes for the active tab, if anything.
    const activeSceneDoc: SceneDoc | null = !activeRep ? null
        : activeIsBorrowed ? inheritedDoc
        : activeIsInterior ? sceneDoc
        : merged && activeRep.uid === merged.baseRep.uid ? sceneDoc
        : null
    // Borrowed from the floor, the lot's rooms are depicted inside the floor's
    // bundle with no parent of their own: the canvas needs the lot's subtree to
    // frame the lot rather than the whole floor.
    const subjectUid = activeSceneDoc?.subjectAssetUid
    const subjectDescendantsQuery = useQuery({
        queryKey: ['plan-subject-descendants', org, subjectUid],
        queryFn: async () => new Set((await fetchAllPages<{ uid: string }>(
            '/api/v1/organizations/{orgCode}/assets',
            { path: { orgCode: org }, query: { ancestor_uid: subjectUid } },
        )).map(a => a.uid)),
        enabled: !!subjectUid,
    })

    // One row per sheet the composed plan stacks, named after the sheet's own
    // title (the DOE "[CVC] …" one), not its owning asset — every sheet of a
    // floor belongs to the floor.
    const layerRows = useMemo(() => {
        if (!activeSceneDoc) return []
        const titles = new Map<string, string>()
        for (const entry of [...(sceneQuery.data?.entries ?? []), ...(floorSceneQuery.data?.entries ?? [])]) {
            const title = (entry.representation?.value as { title?: unknown } | undefined)?.title
            if (typeof title === 'string' && title) titles.set(entry.representation.uid, title)
        }
        const seen = new Set<string>()
        const rows: { key: string, label: string }[] = []
        for (const u of activeSceneDoc.underlays) {
            const key = u.registrationUid ?? u.repUid
            if (seen.has(key)) continue
            seen.add(key)
            rows.push({ key, label: titles.get(u.repUid) || u.assetName || t('planEditor.layers.underlays') })
        }
        return rows
    }, [activeSceneDoc, sceneQuery.data, floorSceneQuery.data, t])
    const layerKinds = useMemo(() => {
        if (!activeSceneDoc) return []
        // Risers draw in the networks layer, with the runs of their family.
        const kinds = new Set(activeSceneDoc.elements.map((el) => (el.kind === 'riser' ? 'run' : el.kind)))
        const byLayer = { spaces: 'space', walls: 'wall', openings: 'opening', runs: 'run', equipment: 'equipment' } as const
        return VECTOR_LAYERS.filter((layer) => kinds.has(byLayer[layer]))
    }, [activeSceneDoc])
    // The networks the runs layer holds, by family in catalogue order, each
    // with its label in the UI language and its run count: what the panel
    // lists under "Networks" so a family or a single network can be hidden.
    const layerNetworks = useMemo((): CompositePlanNetworkFamily[] => {
        if (!activeSceneDoc) return []
        const counts = new Map<string, number>()
        for (const el of activeSceneDoc.elements) {
            if (el.kind === 'run' || el.kind === 'riser') counts.set(el.network, (counts.get(el.network) ?? 0) + 1)
        }
        if (counts.size === 0) return []
        const catalogue = activeSceneDoc.networks ?? []
        const families: CompositePlanNetworkFamily[] = []
        const familyOf = (family: string) => {
            let f = families.find((x) => x.family === family)
            if (!f) {
                f = { family, label: t(`planEditor.network_families.${family}`, { defaultValue: family }), networks: [] }
                families.push(f)
            }
            return f
        }
        const nameOf = (name: unknown, code: string) => {
            const m = (name ?? {}) as Record<string, string>
            return m[i18n.language] || m.en || m.fr || code
        }
        for (const n of catalogue) {
            const count = counts.get(n.code)
            if (!count) continue
            familyOf(n.family || 'other').networks.push({ code: n.code, label: nameOf(n.name, n.code), color: n.color, dash: n.dash, count })
            counts.delete(n.code)
        }
        for (const [code, count] of [...counts.entries()].sort(([a], [b]) => a.localeCompare(b))) {
            familyOf('other').networks.push({ code, label: code, count })
        }
        return families
    }, [activeSceneDoc, t, i18n.language])
    const offersLayerPanel = !!activeSceneDoc && (layerRows.length > 1 || (layerRows.length > 0 && layerKinds.length > 0))

    // …but not before the scene has answered. Which tab is the default DEPENDS
    // on the scene — a composite outranks a lone drawing — and writing `?rep=`
    // from the pre-scene default pins the loser: the uid is then a deliberate
    // choice as far as everything downstream can tell. Harmless while the
    // composite absorbed the drawing (the absorbed-rewrite effect below undid
    // it); not harmless on a BORROWED base, which absorbs nothing and so left
    // every lot opening on its own grey rectangle again.
    //
    // `isLoading` is false for a query that is disabled (a non-frame asset), so
    // this waits only where there is something to wait for. The floor's view of
    // a lot is the default when it exists, so it is waited for too — the query
    // is only enabled once the first one has answered, hence `isPending`.
    const sceneSettled = !sceneQuery.isLoading
        && (!floorContextApplies || !floorSceneQuery.isPending)
    useEffect(() => {
        if (!sceneSettled) return
        if (defaultRep && !activeRepUid) {
            setActiveRepUid(defaultRep.uid)
        }
    }, [defaultRep, activeRepUid, setActiveRepUid, sceneSettled])

    // A link to a representation the merged tab absorbed — an issue pointing at
    // the drawn plan, a bookmark from before the merge — lands on the merged
    // tab, which is where that geometry now lives.
    useEffect(() => {
        if (!merged || !activeRepUid) return
        if (activeRepUid !== merged.baseRep.uid && merged.absorbed.has(activeRepUid)) {
            setActiveRepUid(merged.baseRep.uid)
        }
    }, [merged, activeRepUid, setActiveRepUid])

    // Annotations (issues) for the active representation
    const annotationsQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues',
        {
            params: {
                path: { orgCode: org },
                query: {
                    target_entity_type: 'asset_representation',
                    target_entity_uid: activeRep?.uid,
                    type: 'annotation',
                    status: 'draft,open,in_progress,closed',
                    assignee_uid: 'all',
                    limit: 200,
                } as unknown as Record<string, unknown>,
            },
        },
        // The interior composite is no representation: nothing is anchored on it.
        { enabled: !!activeRep?.uid && !activeIsInterior },
    )
    const annotations = useMemo<Issue[]>(
        () => (activeIsInterior ? [] : (annotationsQuery.data?.data ?? []) as Issue[]),
        [annotationsQuery.data, activeIsInterior],
    )

    // Resolve the editing issue from the URL param.
    const editingIssue = useMemo(
        () => (editingAnnotationUid ? annotations.find(a => a.uid === editingAnnotationUid) ?? null : null),
        [annotations, editingAnnotationUid],
    )

    // Doc types lookup
    const { data: documentTypes = [] } = useDocumentTypes(org)
    const planDocType = useMemo(() => {
        // Pick UNIT_PLAN if asset is unit-like; else FLOOR_PLAN
        const wantUnit = isLotTypeCode(assetTypeCode)
        const codes = wantUnit ? ['UNIT_PLAN', 'FLOOR_PLAN'] : ['FLOOR_PLAN', 'UNIT_PLAN']
        for (const c of codes) {
            const dt = documentTypes.find((d: { code?: string }) => d?.code === c)
            if (dt) return dt
        }
        // fallback any plan-like
        return documentTypes.find((d: { code?: string }) => FLOORPLAN_CODES.includes(d?.code ?? ''))
    }, [documentTypes, assetTypeCode])

    const uploadDoc = useUploadDocument(org)
    const detectMutation = $api.useMutation(
        'post',
        '/api/v1/organizations/{orgCode}/documents/{documentUid}/representations/detect',
    )
    const createRepMutation = $api.useMutation(
        'post',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/representations',
    )
    const deleteRepMutation = $api.useMutation(
        'delete',
        '/api/v1/organizations/{orgCode}/representations/{repUid}',
    )

    const refreshReps = useCallback(() => {
        queryClient.invalidateQueries({
            predicate: q => {
                const k = q.queryKey as unknown[]
                return (
                    k.length >= 2 &&
                    k[0] === 'get' &&
                    (k[1] === '/api/v1/organizations/{orgCode}/assets/{assetUid}/representations' ||
                        k[1] === '/api/v1/organizations/{orgCode}/representations/{repUid}')
                )
            },
        })
    }, [queryClient])

    const refreshAnnotations = useCallback(() => {
        queryClient.invalidateQueries({
            predicate: q => {
                const k = q.queryKey as unknown[]
                return k.length >= 2 && k[0] === 'get' && k[1] === '/api/v1/organizations/{orgCode}/issues'
            },
        })
    }, [queryClient])

    const onPick = () => fileInputRef.current?.click()

    const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file) return
        if (file.size > MAX_BYTES) {
            setError(t('plan.too_large', 'File is too large (max 20 MB)'))
            return
        }
        setError(null)
        setUploading(true)

        try {
            const fd = new FormData()
            fd.append('file', file)
            fd.append('asset_uid', assetUid)
            fd.append('subject_asset_uid', assetUid)
            if (planDocType?.uid) fd.append('document_type_uid', planDocType.uid)
            fd.append('name', file.name)

            // 1. Upload document
            const uploaded = await uploadDoc.mutateAsync(fd as unknown as FormData)
            const docUid: string | undefined = (uploaded as { uid?: string })?.uid
            if (!docUid) throw new Error('Upload returned no document UID')

            // 2. Detect candidates and create the plan(s)
            const isPdf = (file.type || '').toLowerCase().includes('pdf') ||
                file.name.toLowerCase().endsWith('.pdf')
            await attachDocumentAsPlan(docUid, isPdf ? 'pdf' : 'image')
        } catch (err: unknown) {
            const msg = (err as { message?: string })?.message
            setError(msg || t('plan.upload_failed', 'Upload failed'))
        } finally {
            setUploading(false)
        }
    }

    // Shared tail of both entry points — a freshly uploaded file and an
    // existing document picked from the asset's documents: detect the plan
    // regions, then create one representation, or open the wizard when the
    // document carries several.
    const attachDocumentAsPlan = async (docUid: string, kind: 'pdf' | 'image') => {
        let candidates: RepresentationCandidate[] = []
        try {
            const detectResp = await detectMutation.mutateAsync({
                params: { path: { orgCode: org, documentUid: docUid } },
                body: { asset_uid: assetUid },
            })
            candidates = detectResp?.candidates ?? []
        } catch {
            // ignore — fallback below
        }

        if (candidates.length === 0) {
            // Defensive default: full-page bbox
            await createRepMutation.mutateAsync({
                params: { path: { orgCode: org, assetUid } },
                body: {
                    kind,
                    document_uid: docUid,
                    value: {
                        sheets: [{ page: 1, bbox: { x: 0, y: 0, w: 1, h: 1 } }],
                        detection: { method: 'imported' },
                    },
                },
            })
            refreshReps()
        } else if (candidates.length === 1) {
            const c = candidates[0]
            await createRepMutation.mutateAsync({
                params: { path: { orgCode: org, assetUid } },
                body: {
                    kind,
                    document_uid: docUid,
                    value: {
                        title: c.suggested_title,
                        sheets: [{ page: c.page, bbox: c.bbox }],
                        detection: { method: c.detection_method, confidence: c.confidence },
                    },
                },
            })
            refreshReps()
        } else {
            // Multi-candidate wizard
            setWizardCandidates({ documentUid: docUid, kind, candidates })
        }
    }

    // An existing document becomes a plan without a second upload. Only a PDF
    // or an image can be rendered as one; anything else is refused up front.
    const onPickExistingDocument = async (docUid: string) => {
        setDocPickerOpen(false)
        setError(null)
        setUploading(true)
        try {
            const { data: doc, error: getErr } = await apiClient.GET(
                '/api/v1/organizations/{orgCode}/documents/{documentUid}',
                { params: { path: { orgCode: org, documentUid: docUid } } },
            )
            if (getErr || !doc) throw new Error(t('plan.attach_failed', 'Could not use this document'))
            const mime = (doc.file_mime_type || '').toLowerCase()
            const name = (doc.file_name || doc.name || '').toLowerCase()
            const isPdf = mime.includes('pdf') || name.endsWith('.pdf')
            if (!isPdf && !mime.startsWith('image/')) {
                setError(t('plan.unsupported_document', 'This document is not a PDF or an image'))
                return
            }
            await attachDocumentAsPlan(docUid, isPdf ? 'pdf' : 'image')
        } catch (err: unknown) {
            const msg = (err as { message?: string })?.message
            setError(msg || t('plan.attach_failed', 'Could not use this document'))
        } finally {
            setUploading(false)
        }
    }

    // Documents already backing one of this asset's plans stay listed but
    // greyed out in the picker.
    const usedDocumentUids = useMemo(
        () => new Set(representations.map(r => r.document_uid).filter((u): u is string => Boolean(u))),
        [representations],
    )
    const docPickerLabels = useMemo<AttachDocumentControlLabels>(() => ({
        attachExisting: t('plan.pick_existing', 'Use an existing document'),
        upload: t('plan.upload', 'Upload plan'),
        uploading: t('plan.uploading', 'Uploading…'),
        pickDocumentTitle: t('plan.pick_existing_title', 'Choose a document for the plan'),
        searchPlaceholder: t('plan.pick_existing_search', "Search this asset's documents or paste a document link"),
        noDocumentsFound: t('plan.pick_existing_empty', 'No document found'),
        alreadyAttached: t('plan.pick_existing_used', 'Already a plan'),
        otherDocuments: t('plan.pick_existing_others', 'Elsewhere in the organization'),
    }), [t])
    const docPicker = docPickerOpen ? (
        <DocumentPickerDialog
            org={org}
            assetUid={assetUid}
            open={docPickerOpen}
            onOpenChange={setDocPickerOpen}
            linkedUids={usedDocumentUids}
            testidPrefix="plan-doc"
            labels={docPickerLabels}
            onPick={onPickExistingDocument}
        />
    ) : null

    const handleWizardConfirm = async (
        choices: { candidate: RepresentationCandidate; assetUid: string; skip: boolean }[],
    ) => {
        if (!wizardCandidates) return
        const { documentUid, kind } = wizardCandidates
        for (const ch of choices) {
            if (ch.skip) continue
            await createRepMutation.mutateAsync({
                params: { path: { orgCode: org, assetUid: ch.assetUid } },
                body: {
                    kind,
                    document_uid: documentUid,
                    value: {
                        title: ch.candidate.suggested_title,
                        sheets: [{ page: ch.candidate.page, bbox: ch.candidate.bbox }],
                        detection: {
                            method: ch.candidate.detection_method,
                            confidence: ch.candidate.confidence,
                        },
                    },
                },
            })
        }
        setWizardCandidates(null)
        refreshReps()
    }

    const handleDeleteRep = async () => {
        if (!deleteRepUid) return
        try {
            await deleteRepMutation.mutateAsync({
                params: { path: { orgCode: org, repUid: deleteRepUid } },
            })
            if (activeRepUid === deleteRepUid) setActiveRepUid(null)
            refreshReps()
        } finally {
            setDeleteRepUid(null)
        }
    }

    // ---- Loading / empty states ----
    if (repsQuery.isLoading) {
        return (
            <div className="text-muted-foreground" data-testid="plan-loading">
                {t('plan.loading', 'Loading plan…')}
            </div>
        )
    }

    if (representations.length === 0 && !merged && !inheritedView && !interiorRep && !canWriteAssets) {
        return (
            <EmptyState
                icon={mdiFileImage}
                title={t('plan.none_yet')}
                data-testid="plan-empty-readonly"
            />
        )
    }

    if (representations.length === 0 && !merged && !inheritedView && !interiorRep) {
        return (
            <>
                <PlanDropZone
                    onPick={onPick}
                    onPickExisting={() => setDocPickerOpen(true)}
                    uploading={uploading}
                    error={error}
                    fileInputRef={fileInputRef}
                    onFileChange={onFileChange}
                    org={org}
                    assetUid={assetUid}
                    assetTypeCode={assetTypeCode}
                />
                {docPicker}
                {wizardCandidates && (
                    <PlanCandidateWizard
                        org={org}
                        defaultAssetUid={assetUid}
                        documentUid={wizardCandidates.documentUid}
                        candidates={wizardCandidates.candidates}
                        onCancel={() => setWizardCandidates(null)}
                        onConfirm={handleWizardConfirm}
                    />
                )}
            </>
        )
    }

    return (
        <div className="space-y-4">
            {/* Spec 2026-05-06-02 §4 — surfaces / occupation property axes
                land here, above the plan visualisation, since they describe
                the same spatial entity the plan renders.

                Rendered only when the axis actually has something to show. On a
                floor these two panels were both noise: no property carries the
                `surfaces` category, so the first showed a bare "No properties
                found", and the second showed nothing but the computed
                `unit_is_occupied` flag, which is meaningless above a unit. Two
                empty cards pushed the plan itself below the fold. */}
            {hasSurfaceProperties && (
                <div data-testid="plan-viewer-properties-surfaces">
                    <AssetProperties org={org} assetUid={assetUid} categoryCodes={['surfaces']} />
                </div>
            )}
            {hasOccupationProperties && (
                <div data-testid="plan-viewer-properties-occupation">
                    <AssetProperties org={org} assetUid={assetUid} categoryCodes={['occupation']} />
                </div>
            )}
            <div className="bg-card rounded-lg border border-border p-4 space-y-3" data-testid="plan-viewer">
            {/* Tabs — always render so the "+" tab is available, even when only one rep exists. */}
            {representations.length > 0 || inheritedView || interiorRep ? (
                <Tabs value={activeRep?.uid ?? ''} onValueChange={v => {
                    if (v === '__add__') {
                        onPick()
                    } else if (v === '__attach__') {
                        setDocPickerOpen(true)
                    } else {
                        setActiveRepUid(v)
                    }
                }}>
                    {/* `h-auto`: TabsList is a fixed `h-10`, so a second row of
                        wrapped tabs spilled out of the grey strip (#1546). */}
                    <TabsList className="flex h-auto flex-wrap justify-start gap-1" data-testid="plan-tabs">
                        {tabReps.map((rep, i) => {
                            // Renamed only when the merge actually swallowed
                            // another tab: a lone scan that merely gained an
                            // overlay is still best named after its document.
                            const isMerged = merged?.baseRep.uid === rep.uid && merged.absorbed.size > 1
                            // A borrowed base is named after the frame it came
                            // from: "Full plan" would claim the drawing is this
                            // asset's, and the reader has to know they are
                            // looking at their lot inside its floor.
                            const isInherited = isBorrowedTab(rep.uid)
                            // The lot's own frame, composed: its interior.
                            const isInterior = !!interiorRep && rep.uid === interiorRep.uid
                            const v = rep.value as { title?: string; sheets?: { page?: number }[] } | undefined
                            const sheet = v?.sheets?.[0]
                            // The merged tab is no longer "the scan": it is the
                            // plan, scan and drawn geometry together, so it
                            // drops the source document's own title.
                            const label = isInherited
                                ? t('plan.tab_label_in_frame', {
                                    name: inheritedView?.frameName ?? '',
                                    defaultValue: 'In {{name}}',
                                })
                                : isInterior
                                ? t('plan.tab_label_interior', 'Interior')
                                : isMerged
                                ? t('plan.tab_label_merged', 'Full plan')
                                : v?.title
                                || (rep.kind === 'vector'
                                    ? t('plan.tab_label_vector', 'Drawn plan')
                                    : t('plan.tab_label_default', { page: sheet?.page ?? 1, defaultValue: 'Plan #{{page}}', }))
                                || `Plan ${i + 1}`
                            const icon = isMerged || isInherited || isInterior ? mdiLayersTriple
                                : rep.kind === 'pdf' ? mdiFilePdfBox
                                : rep.kind === 'vector' ? mdiVectorSquare
                                : mdiFileImage
                            return (
                                <TabsTrigger
                                    key={rep.uid}
                                    value={rep.uid}
                                    data-testid={`plan-tab-${rep.uid}`}
                                >
                                    <AppIcon
                                        path={icon}
                                        size="sm"
                                        className="mr-1"
                                    />
                                    {label}
                                </TabsTrigger>
                            )
                        })}
                        <TabsTrigger
                            value="__add__"
                            data-testid="plan-tab-add"
                            aria-label={t('plan.add_tab', 'Add plan')}
                            title={t('plan.add_tab', 'Add plan')}
                        >
                            <AppIcon path={mdiPlus} size="sm" />
                        </TabsTrigger>
                        {/* `plan-tab-<uid>` is the REPRESENTATION namespace: specs
                            count the plans on screen with
                            `[data-testid^="plan-tab-"]` (minus the legacy
                            `plan-tab-add`), so a control must not borrow that
                            prefix or it is counted as a plan. */}
                        <TabsTrigger
                            value="__attach__"
                            data-testid="plan-attach-tab"
                            aria-label={t('plan.pick_existing', 'Use an existing document')}
                            title={t('plan.pick_existing', 'Use an existing document')}
                        >
                            <AppIcon path={mdiPaperclip} size="sm" />
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            ) : null}
            {docPicker}

            {/* Hidden file input for "+ tab" */}
            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED}
                onChange={onFileChange}
                className="hidden"
                data-testid="plan-file-input"
            />

            {/* Toolbar */}
            {activeRep && (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {!activeIsInterior && (
                            <>
                                <AppIcon path={mdiCommentTextOutline} size="sm" />
                                <span data-testid="plan-annotation-count">
                                    {t('plan.annotation_count', { count: annotations.length, defaultValue: '{{count}} annotations' })}
                                </span>
                            </>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        {offersLayerPanel && (
                            <Button
                                variant={layerPanelOpen ? 'default' : 'outline'}
                                size="icon"
                                aria-label={t('compositePlan.toggle_layers')}
                                title={t('compositePlan.toggle_layers')}
                                aria-pressed={layerPanelOpen}
                                onClick={toggleLayerPanel}
                                data-testid="plan-layers-toggle"
                            >
                                <AppIcon path={mdiLayersOutline} size="sm" />
                            </Button>
                        )}
                        {activeSceneDoc && !activeIsInterior && (
                            <Button
                                variant={showVector ? 'default' : 'outline'}
                                size="icon"
                                aria-label={t('plan.toggle_drawn', 'Show drawn plan')}
                                title={t('plan.toggle_drawn', 'Show drawn plan')}
                                aria-pressed={showVector}
                                onClick={() => setShowVector(v => !v)}
                                data-testid="plan-toggle-vector"
                            >
                                <AppIcon path={mdiVectorSquare} size="sm" />
                            </Button>
                        )}
                        {/* The document the plan was made from (a scan, a PDF
                            page). A drawn plan has none, nor does a base
                            borrowed from an ancestor's frame. */}
                        {activeRep.document_uid && (
                            <Button variant="outline" size="icon" asChild data-testid="plan-open-document">
                                <Link
                                    to="/$org/documents/$docUid"
                                    params={{ org, docUid: activeRep.document_uid }}
                                    aria-label={t('plan.open_document', 'Open the source document')}
                                    title={t('plan.open_document', 'Open the source document')}
                                >
                                    <AppIcon path={SECTION_ICONS.documents} size="sm" />
                                </Link>
                            </Button>
                        )}
                        <PlanEditorLink
                            org={org}
                            assetUid={assetUid}
                            assetTypeCode={assetTypeCode}
                            variant="outline"
                            label={t('plan.edit_plan', 'Edit plan')}
                        />
                        {/* A borrowed base belongs to an ancestor's frame.
                            `DELETE /representations/{uid}` is org-scoped, so
                            offering it here would let a reader of one lot
                            destroy the whole floor's scan. */}
                        {/* The interior composite is no representation at all. */}
                        {!activeIsBorrowed && !activeIsInterior && (
                            <Button
                                variant="outline"
                                size="icon"
                                aria-label={t('plan.delete', 'Delete plan')}
                                title={t('plan.delete', 'Delete plan')}
                                onClick={() => setDeleteRepUid(activeRep.uid)}
                                data-testid="plan-delete-btn"
                            >
                                <AppIcon path={mdiDelete} size="sm" />
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Colour by: rooms and equipment drawn on this plan, by grouper unless
                the reader picks another thematic. */}
            {activeRep && <ThematicBar org={org} state={thematic} />}

            {/* Canvas */}
            {activeRep && (
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
                {offersLayerPanel && layerPanelOpen && (
                    <CompositePlanLayerPanel
                        className="max-h-[320px] rounded-md border border-border bg-card lg:max-h-[640px] lg:w-72 lg:shrink-0"
                        underlays={layerRows}
                        vectorLayers={layerKinds}
                        layers={layerVisibility}
                        onChange={changeLayerVisibility}
                        networks={layerNetworks}
                    />
                )}
                <div className="min-w-0 flex-1">
                <PlanCanvas
                    org={org}
                    representation={activeRep}
                    annotations={annotations}
                    onAnnotationClick={iss => setEditingAnnotation(iss.uid)}
                    onAnnotationDraft={draft => setDraftAnnotation(draft)}
                    draft={draftAnnotation}
                    editingIssueUid={editGeometryActive && editingIssue ? editingIssue.uid : null}
                    editingGeometry={editGeometryActive ? editingGeometry : null}
                    onEditingGeometryChange={g => setEditingGeometry(g)}
                    scene={activeSceneDoc}
                    showGeometry={showVector}
                    layerVisibility={offersLayerPanel ? layerVisibility : undefined}
                    typeLabelOf={localizedTypeLabel}
                    onSceneAssetClick={followSceneAsset}
                    subjectAssetUid={activeSceneDoc?.subjectAssetUid}
                    viewedAssetUid={assetUid}
                    subjectDescendantUids={subjectDescendantsQuery.data}
                    annotatable={!activeIsBorrowed && !activeIsInterior}
                    colourIndex={thematic.choice.key ? thematic.index : undefined}
                    isolated={thematic.choice.isolated}
                />
                </div>
                </div>
            )}

            {/* Edit-geometry overlay — small "Done" button while dragging vertices */}
            {editGeometryActive && (
                <div
                    className="flex items-center gap-2 text-sm"
                    data-testid="plan-edit-geometry-bar"
                >
                    <span className="text-muted-foreground">
                        {t('plan.edit_geometry_hint', 'Drag the handles to reshape · click Done to save the new shape')}
                    </span>
                    <button
                        className="ml-auto inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1 text-sm hover:bg-muted"
                        onClick={() => setEditGeometryActive(false)}
                        data-testid="plan-edit-geometry-done"
                    >
                        {t('plan.edit_geometry_done', 'Done')}
                    </button>
                    <button
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1 text-sm hover:bg-muted"
                        onClick={() => {
                            // Restore from issue and exit edit mode without saving.
                            const props = editingIssue
                                ? (editingIssue.properties as { annotation?: components['schemas']['AnnotationProperties'] } | undefined)?.annotation
                                : null
                            if (props?.geometry) setEditingGeometry(props.geometry)
                            setEditGeometryActive(false)
                        }}
                        data-testid="plan-edit-geometry-cancel"
                    >
                        {t('plan.cancel', 'Cancel')}
                    </button>
                </div>
            )}

            {/* Annotations list */}
            {activeRep && !activeIsInterior && (
                <PlanAnnotationsList
                    annotations={annotations}
                    onSelect={iss => setEditingAnnotation(iss.uid)}
                    annotatable={!activeIsBorrowed}
                />
            )}

            {error && <div className="text-destructive text-sm" data-testid="plan-error">{error}</div>}

            {/* Wizard */}
            {wizardCandidates && (
                <PlanCandidateWizard
                    org={org}
                    defaultAssetUid={assetUid}
                    documentUid={wizardCandidates.documentUid}
                    candidates={wizardCandidates.candidates}
                    onCancel={() => setWizardCandidates(null)}
                    onConfirm={handleWizardConfirm}
                />
            )}

            {/* Tagging panel — create */}
            {draftAnnotation && activeRep && (
                <AnnotationTaggingPanel
                    org={org}
                    representationUid={activeRep.uid}
                    assetUid={assetUid}
                    mode="create"
                    draft={draftAnnotation}
                    onClose={() => setDraftAnnotation(null)}
                    onSaved={() => {
                        setDraftAnnotation(null)
                        refreshAnnotations()
                    }}
                />
            )}

            {/* Tagging panel — edit. Suppressed while geometry edit is active. */}
            {editingIssue && activeRep && !editGeometryActive && (
                <AnnotationTaggingPanel
                    org={org}
                    representationUid={activeRep.uid}
                    assetUid={assetUid}
                    mode="edit"
                    issue={editingIssue}
                    pendingGeometry={editingGeometry ?? undefined}
                    onEditGeometry={() => {
                        const props = (editingIssue.properties as { annotation?: components['schemas']['AnnotationProperties'] } | undefined)?.annotation
                        if (props?.geometry) {
                            setEditingGeometry(props.geometry)
                            setEditGeometryActive(true)
                        }
                    }}
                    onClose={() => {
                        setEditingAnnotation(null)
                        setEditingGeometry(null)
                    }}
                    onSaved={() => {
                        setEditingAnnotation(null)
                        setEditingGeometry(null)
                        refreshAnnotations()
                    }}
                />
            )}

            {/* Delete confirm */}
            <AlertDialog open={!!deleteRepUid} onOpenChange={open => !open && setDeleteRepUid(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('plan.delete_confirm_title', 'Delete this plan?')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('plan.delete_confirm_desc', 'The source document is kept. Annotations on this plan will also be removed.')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="plan-delete-cancel">{t('plan.cancel', 'Cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteRep}
                            data-testid="plan-delete-confirm"
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {t('plan.delete', 'Delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            </div>

            {/*
              * The frame's registrations — the precise, numeric fallback to the
              * plan editor's direct manipulation, and the way into anchor-pair
              * calibration (spec 2026-08-22-03 §6). Only floors and units
              * anchor a frame the editor can open; the composite plan tab
              * carries the same panel for sites and buildings.
              */}
            {assetTypeCode && PLAN_EDITABLE_TYPE_CODES.includes(lotBaseTypeCode(assetTypeCode)) && (
                <FrameRegistrationsPanel org={org} assetUid={assetUid} />
            )}
        </div>
    )
}

interface PlanAnnotationsListProps {
    annotations: Issue[]
    onSelect: (iss: Issue) => void
    /**
     * False on a borrowed composite — the drawing belongs to an ancestor's
     * frame, so there is no toolbar to point an empty list at (spec
     * 2026-09-17-01).
     */
    annotatable?: boolean
}

function PlanAnnotationsList({ annotations, onSelect, annotatable = true }: PlanAnnotationsListProps) {
    const { t } = useTranslation('assets')
    return (
        <div className="border-t border-border pt-3 space-y-2" data-testid="plan-annotation-list">
            <div className="text-sm font-medium text-foreground">
                {t('plan.annotation_list_title', 'Annotations')}
            </div>
            {annotations.length === 0 ? (
                <EmptyState
                    variant="inline"
                    className="py-4"
                    description={
                        annotatable
                            ? t('plan.annotation_list_empty', 'No annotations on this plan yet. Use the toolbar to draw one.')
                            : t('plan.annotation_list_borrowed', 'This drawing belongs to another plan. Switch to this asset\u2019s own plan to annotate it.')
                    }
                    data-testid="plan-annotation-list-empty"
                />
            ) : (
                <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
                    {annotations.map(iss => {
                        const ann = (iss.properties as { annotation?: { style?: { color?: string }; geometry?: { kind?: string } } } | undefined)?.annotation
                        const color = ann?.style?.color || 'var(--primary)'
                        const kind = ann?.geometry?.kind || 'point'
                        const title = iss.title || t('plan.annotation_unnamed', 'Untitled annotation')
                        return (
                            <li key={iss.uid}>
                                <button
                                    type="button"
                                    onClick={() => onSelect(iss)}
                                    className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                                    data-testid={`plan-annotation-list-item-${iss.uid}`}
                                >
                                    <span
                                        aria-hidden
                                        className="inline-block h-3 w-3 rounded-full border border-border flex-shrink-0"
                                        style={{ backgroundColor: color }}
                                    />
                                    <span className="truncate flex-1 text-foreground">{title}</span>
                                    <span className="text-xs text-muted-foreground flex-shrink-0">
                                        {t(`plan.annotation_geom_${kind}`, kind)}
                                    </span>
                                </button>
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}

interface PlanDropZoneProps {
    onPick: () => void
    onPickExisting: () => void
    uploading: boolean
    error: string | null
    fileInputRef: React.RefObject<HTMLInputElement | null>
    onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    org: string
    assetUid: string
    assetTypeCode?: string
}

function PlanDropZone({ onPick, onPickExisting, uploading, error, fileInputRef, onFileChange, org, assetUid, assetTypeCode }: PlanDropZoneProps) {
    const { t } = useTranslation('assets')
    const [dragOver, setDragOver] = useState(false)

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setDragOver(false)
        const file = e.dataTransfer.files?.[0]
        if (file && fileInputRef.current) {
            const dt = new DataTransfer()
            dt.items.add(file)
            fileInputRef.current.files = dt.files
            const ev = new Event('change', { bubbles: true })
            fileInputRef.current.dispatchEvent(ev)
        }
    }

    return (
        <div
            className={`bg-card rounded-lg border-2 border-dashed p-12 flex flex-col items-center justify-center gap-3 text-center cursor-pointer transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
            onClick={onPick}
            onDragOver={e => {
                e.preventDefault()
                setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            data-testid="plan-drop-zone"
            role="button"
            tabIndex={0}
        >
            <AppIcon tone="muted" path={mdiFileImage} size="2xl" />
            <div className="text-foreground font-medium" data-testid="plan-empty-caption">
                {t('plan.drop_caption', 'Drop a plan (PDF or image)')}
            </div>
            <div className="text-sm text-muted-foreground">{t('plan.browse', 'or click to browse')}</div>
            <div className="text-xs text-muted-foreground">{t('plan.formats', 'PNG, JPG, PDF · max 20 MB')}</div>
            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED}
                onChange={onFileChange}
                className="hidden"
                data-testid="plan-file-input"
            />
            <Button
                variant="default"
                size="sm"
                disabled={uploading}
                onClick={e => {
                    e.stopPropagation()
                    onPick()
                }}
                data-testid="plan-upload-btn"
            >
                {uploading ? t('plan.uploading', 'Uploading…') : t('plan.upload', 'Upload plan')}
            </Button>
            <Button
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={e => {
                    e.stopPropagation()
                    onPickExisting()
                }}
                data-testid="plan-doc-pick-btn"
            >
                <AppIcon path={mdiPaperclip} size="sm" className="mr-1" />
                {t('plan.pick_existing', 'Use an existing document')}
            </Button>
            <PlanEditorLink
                org={org}
                assetUid={assetUid}
                assetTypeCode={assetTypeCode}
                variant="outline"
                label={t('plan.draw_from_scratch', 'Draw from scratch')}
            />
            {error && <div className="text-destructive text-sm" data-testid="plan-error">{error}</div>}
        </div>
    )
}

